import config from "../config";
import store from "../store.js";

import DashboardLayout from "@/views/GeneralViews/Layout.vue";
import AuthLayout from "@/views/Pages/AuthLayout.vue";
// GeneralViews
import NotFound from "@/views/GeneralViews/NotFoundPage.vue";

// Pages
import Overview from "@/views/Overview/OverviewPage.vue";
const User = () =>
  import(/* webpackChunkName: "pages" */ "@/views/Pages/UserProfile.vue");
const Login = () =>
  import(/* webpackChunkName: "pages" */ "@/views/Pages/Login.vue");
const Register = () =>
  import(/* webpackChunkName: "pages" */ "@/views/Pages/Register.vue");
const VerifyEmail = () =>
  import(/* webpackChunkName: "pages" */ "@/views/Pages/VerifyEmail.vue");
const ForgottenPassword = () =>
  import(/* webpackChunkName: "pages" */ "@/views/Pages/ForgottenPassword.vue");
const ResetPassword = () =>
  import(/* webpackChunkName: "pages" */ "@/views/Pages/ResetPassword.vue");
const AccountSelect = () =>
  import(/* webpackChunkName: "pages" */ "@/views/Pages/AccountSelect.vue");
const AccountCreate = () =>
  import(/* webpackChunkName: "pages" */ "@/views/Pages/AccountCreate.vue");
const Account = () =>
  import(
    /* webpackChunkName: "pages" */ "@/views/Pages/Account/AccountPage.vue"
  );
const AccountInvitationAccept = () =>
  import(
    /* webpackChunkName: "pages" */ "@/views/Pages/AccountInvitationAccept.vue"
  );
const CompanyCreate = () =>
  import(
    /* webpackChunkName: "pages" */ "@/views/Pages/Company/CompanyCreate.vue"
  );
const CompanyView = () =>
  import(
    /* webpackChunkName: "pages" */ "@/views/Pages/Company/CompanyView.vue"
  );
const CompanyEdit = () =>
  import(
    /* webpackChunkName: "pages" */ "@/views/Pages/Company/CompanyEdit.vue"
  );
const DocumentCreate = () =>
  import(
    /* webpackChunkName: "pages" */ "@/views/Pages/Document/DocumentCreate.vue"
  );
const DocumentView = () =>
  import(
    /* webpackChunkName: "pages" */ "@/views/Pages/Document/DocumentView.vue"
  );
const DocumentEdit = () =>
  import(
    /* webpackChunkName: "pages" */ "@/views/Pages/Document/DocumentEdit.vue"
  );

// auth guards
function checkForSelectedAccount(to, from, next) {
  // check whether user has an operator set to localstorage
  const account = store.state.account;
  if (account) {
    if (store.state.membership) {
      return next();
    } else {
      // load account user and set it
      window.axios
        .get(`/v1/accounts/${account.id}/members/current`)
        .then(response => {
          store.commit("SET_MEMBERSHIP", response.data);
          return next();
        })
        .catch(err => {
          console.error("error loading account user: ", err); // TODO: handle this error better?
          return next();
        });
    }
  } else {
    return next("/select-account");
  }
}

function clearSelectedAccount(to, from, next) {
  store.commit("SET_ACCOUNT", null);
  store.commit("SET_MEMBERSHIP", null);
  return next();
}

function clearAccessToken(to, from, next) {
  localStorage.removeItem(`${config.namespace}__auth_token`);
  return next();
}

const authPages = {
  path: "/",
  component: AuthLayout,
  name: "Authentication",
  children: [
    {
      path: "/login",
      name: "Login",
      component: Login,
      meta: {
        auth: false
      },
      beforeEnter: clearAccessToken
    },
    {
      path: "/select-account",
      name: "Select account",
      component: AccountSelect,
      meta: {
        auth: true
      },
      beforeEnter: clearSelectedAccount
    },
    {
      path: "/create-account",
      name: "Create account",
      component: AccountCreate,
      meta: {
        auth: true
      },
      beforeEnter: clearSelectedAccount
    },
    {
      path: "/accept-account-invitation/:invitationId",
      props: route => ({
        accountId: route.query.accountId,
        invitationId: route.params.invitationId,
        token: route.query.token
      }),
      name: "Accept account invitation",
      component: AccountInvitationAccept,
      meta: {
        auth: null // accept both logged in and logged out users
      },
      beforeEnter: clearSelectedAccount
    },
    {
      path: "/register",
      name: "Register",
      component: Register,
      meta: {
        auth: false
      }
    },
    {
      path: "/forgot-password",
      name: "Forgotten Password",
      component: ForgottenPassword,
      meta: {
        auth: false
      }
    },
    {
      path: "/reset-password",
      props: route => ({
        ticketId: route.query.ticketId,
        token: route.query.token
      }),
      name: "Reset Password",
      component: ResetPassword,
      meta: {
        auth: false
      }
    },
    {
      path: "/verify-email",
      props: route => ({
        userId: route.query.userId,
        emailAddress: route.query.address,
        token: route.query.token
      }),
      name: "Verify Email",
      component: VerifyEmail,
      meta: {
        auth: null // can be accessed by logged in and logged out users
      }
    }
  ]
};

const routes = [
  {
    path: "/",
    name: "Home",
    redirect: "/dashboard",
    component: DashboardLayout,
    beforeEnter: checkForSelectedAccount,
    children: [
      {
        path: "dashboard",
        name: "dashboard",
        components: { default: Overview },
        meta: {
          auth: true
        }
      },
      {
        path: "companies",
        name: "companies",
        redirect: "/"
      },
      {
        path: "companies/create",
        name: "Create Company",
        components: { default: CompanyCreate },
        meta: {
          auth: true
        }
      },
      {
        path: "/companies/:companyId",
        props: route => ({
          companyId: route.params.companyId
        }),
        name: "View Company",
        component: CompanyView,
        meta: {
          auth: true
        }
      },
      {
        path: "/companies/:companyId/edit",
        props: route => ({
          companyId: route.params.companyId
        }),
        name: "Edit Company",
        component: CompanyEdit,
        meta: {
          auth: true
        }
      },
      {
        path: "/companies/:companyId/documents/create",
        props: route => ({
          companyId: route.params.companyId
        }),
        name: "Create Document",
        component: DocumentCreate,
        meta: {
          auth: true
        }
      },
      {
        path: "/companies/:companyId/documents/:documentId",
        props: route => ({
          companyId: route.params.companyId,
          documentId: route.params.documentId
        }),
        name: "View Document",
        component: DocumentView,
        meta: {
          auth: true
        }
      },
      {
        path: "/companies/:companyId/documents/:documentId/edit",
        props: route => ({
          companyId: route.params.companyId,
          documentId: route.params.documentId
        }),
        name: "Edit Document",
        component: DocumentEdit,
        meta: {
          auth: true
        }
      },
      {
        path: "user",
        name: "user",
        components: { default: User },
        meta: {
          auth: true
        }
      },
      {
        path: "account",
        name: "account",
        components: { default: Account },
        meta: {
          auth: true
        }
      }
    ]
  },
  authPages
];

export default routes;
